import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { useBrand } from '../../lib/brandContext';
import * as adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { Shield, Users, BarChart3, Palette, Flag, ChevronLeft, Search, UserCheck, UserX, Crown, ArrowDownCircle, Eye, Mail, Megaphone, RotateCcw, Clock, Download } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const downloadCsv = async (endpoint, filename) => {
  const token = localStorage.getItem('token');
  try {
    const res = await fetch(`${API}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  } catch (error) {
    toast.error('Failed to export CSV');
  }
};

const AdminConsole = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    if (user && user.role !== 'ADMIN') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, navigate]);

  if (!user || user.role !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-brand-text mb-2">403 Forbidden</h2>
          <p className="text-brand-muted">Admin access required</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'moderation', label: 'Moderation', icon: Flag },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'activity', label: 'Activity', icon: BarChart3 },
    { id: 'audit', label: 'Audit Log', icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-console">
      <header className="bg-brand-surface border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-brand-gold" />
              <h1 className="font-display text-xl text-brand-text tracking-wide">Admin Console</h1>
            </div>
            <button
              onClick={() => navigate('/business/dashboard')}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 text-brand-text hover:bg-white/5 text-sm"
              data-testid="admin-back-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              Dashboard
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-1 mb-6 border-b border-zinc-800 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-brand-gold border-b-2 border-brand-gold'
                    : 'text-brand-muted hover:text-brand-text'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'moderation' && <ModerationTab />}
        {activeTab === 'branding' && <BrandingTab />}
        {activeTab === 'activity' && <ActivityTab />}
        {activeTab === 'audit' && <AuditTab />}
      </div>
    </div>
  );
};

// ============= USERS TAB =============
const UsersTab = () => {
  const { user: currentAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: '', status: '', search: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [broadcastRole, setBroadcastRole] = useState('ALL');
  const [actionLoading, setActionLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.getUsers(filters);
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadUsers(); }, []);

  const handleSearch = () => loadUsers();

  const handleViewProfile = async (user) => {
    try {
      const profile = await adminApi.getUserProfile(user.id);
      setUserProfile(profile);
      setSelectedUser(user);
      setProfileModalOpen(true);
    } catch (error) {
      toast.error('Failed to load user profile');
    }
  };

  const handleBan = async () => {
    if (!selectedUser || !banReason.trim()) { toast.error('Please provide a ban reason'); return; }
    try {
      setActionLoading(true);
      await adminApi.banUser(selectedUser.id, banReason);
      toast.success('User banned successfully');
      setBanModalOpen(false); setBanReason(''); setProfileModalOpen(false);
      loadUsers();
    } catch (error) { toast.error(error.message || 'Failed to ban user'); }
    finally { setActionLoading(false); }
  };

  const handleUnban = async (user) => {
    try {
      await adminApi.unbanUser(user.id);
      toast.success('User unbanned successfully');
      setProfileModalOpen(false);
      loadUsers();
    } catch (error) { toast.error('Failed to unban user'); }
  };

  const handlePromote = async (user) => {
    try {
      setActionLoading(true);
      await adminApi.promoteUser(user.id);
      toast.success(`${user.name} promoted to Admin`);
      setProfileModalOpen(false);
      loadUsers();
    } catch (error) { toast.error(error.message || 'Failed to promote user'); }
    finally { setActionLoading(false); }
  };

  const handleDemote = async (user) => {
    try {
      setActionLoading(true);
      await adminApi.demoteUser(user.id);
      toast.success(`${user.name} demoted from Admin`);
      setProfileModalOpen(false);
      loadUsers();
    } catch (error) { toast.error(error.message || 'Failed to demote user'); }
    finally { setActionLoading(false); }
  };

  const handleDeactivate = async (user) => {
    try {
      setActionLoading(true);
      await adminApi.deactivateUser(user.id);
      toast.success(`${user.name} deactivated`);
      setProfileModalOpen(false);
      loadUsers();
    } catch (error) { toast.error(error.message || 'Failed to deactivate user'); }
    finally { setActionLoading(false); }
  };

  const handleReactivate = async (user) => {
    try {
      setActionLoading(true);
      await adminApi.reactivateUser(user.id);
      toast.success(`${user.name} reactivated`);
      setProfileModalOpen(false);
      loadUsers();
    } catch (error) { toast.error(error.message || 'Failed to reactivate user'); }
    finally { setActionLoading(false); }
  };

  const handleSendMessage = async () => {
    if (!selectedUser || !messageSubject.trim() || !messageBody.trim()) { toast.error('Please fill in all fields'); return; }
    try {
      setActionLoading(true);
      await adminApi.sendDirectMessage(selectedUser.id, messageSubject, messageBody);
      toast.success('Message sent successfully');
      setMessageModalOpen(false); setMessageSubject(''); setMessageBody('');
    } catch (error) { toast.error('Failed to send message'); }
    finally { setActionLoading(false); }
  };

  const handleBroadcast = async () => {
    if (!messageSubject.trim() || !messageBody.trim()) { toast.error('Please fill in all fields'); return; }
    try {
      setActionLoading(true);
      const result = await adminApi.sendBroadcast(messageSubject, messageBody, broadcastRole);
      toast.success(`Broadcast sent to ${result.count} users`);
      setBroadcastModalOpen(false); setMessageSubject(''); setMessageBody('');
    } catch (error) { toast.error('Failed to send broadcast'); }
    finally { setActionLoading(false); }
  };

  const getStatusBadges = (user) => {
    const badges = [];
    if (user.banned) badges.push({ label: 'BANNED', color: 'bg-red-600/20 text-red-500 border-red-600/40' });
    if (user.deactivated) badges.push({ label: 'DEACTIVATED', color: 'bg-orange-600/20 text-orange-400 border-orange-600/40' });
    if (user.role === 'ADMIN') badges.push({ label: 'ADMIN', color: 'bg-purple-600/20 text-purple-400 border-purple-600/40' });
    return badges;
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="rounded-xl border border-white/10 bg-[#111317] p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
            <input
              type="text"
              placeholder="Search name or email..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text"
              data-testid="admin-users-search"
            />
          </div>
          <select
            value={filters.role}
            onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
            className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text"
            data-testid="admin-users-role-filter"
          >
            <option value="">All Roles</option>
            <option value="BUSINESS">Business</option>
            <option value="SALESPERSON">Salesperson</option>
            <option value="ADMIN">Admin</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text"
            data-testid="admin-users-status-filter"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="banned">Banned</option>
            <option value="deactivated">Deactivated</option>
          </select>
          <button onClick={handleSearch} className="px-4 py-2 rounded-lg bg-brand-gold text-black hover:bg-brand-gold/90" data-testid="admin-users-search-btn">
            Search
          </button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setBroadcastModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700"
            data-testid="admin-broadcast-button"
          >
            <Megaphone className="w-4 h-4" />
            Send Broadcast
          </button>
          <button
            onClick={() => downloadCsv('/admin/export/users.csv', 'users.csv')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-brand-text hover:bg-white/5"
            data-testid="admin-export-users-btn"
          >
            <Download className="w-4 h-4" />
            Export Users CSV
          </button>
          <button
            onClick={() => downloadCsv('/admin/export/requests.csv', 'requests.csv')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-brand-text hover:bg-white/5"
            data-testid="admin-export-requests-btn"
          >
            <Download className="w-4 h-4" />
            Export Requests CSV
          </button>
        </div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="text-brand-muted text-center py-12">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-brand-muted">No users found</div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid="admin-user-row" className="rounded-xl border border-white/10 bg-[#111317] p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-brand-text font-semibold truncate">{u.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-brand-muted">{u.role}</span>
                    {getStatusBadges(u).map((b, i) => (
                      <span key={i} className={`text-xs px-2 py-0.5 rounded border ${b.color}`}>{b.label}</span>
                    ))}
                  </div>
                  <div className="text-sm text-brand-muted mt-1">{u.email}</div>
                  <div className="text-xs text-brand-muted mt-0.5">Joined {new Date(u.created_at).toLocaleDateString()}</div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => handleViewProfile(u)} data-testid="admin-user-view" className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-brand-gold/20 text-brand-gold border border-brand-gold/40 hover:bg-brand-gold/30">
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                  <button onClick={() => { setSelectedUser(u); setMessageModalOpen(true); }} data-testid="admin-user-message" className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-blue-600/20 text-blue-500 border border-blue-600/40 hover:bg-blue-600/30">
                    <Mail className="w-3.5 h-3.5" /> Message
                  </button>
                  {!u.banned && u.id !== currentAdmin?.id && (
                    <button onClick={() => { setSelectedUser(u); setBanModalOpen(true); }} data-testid="admin-user-ban" className="px-3 py-1.5 text-sm rounded-lg bg-red-600/20 text-red-500 border border-red-600/40 hover:bg-red-600/30">
                      Ban
                    </button>
                  )}
                  {u.banned && (
                    <button onClick={() => handleUnban(u)} data-testid="admin-user-unban" className="px-3 py-1.5 text-sm rounded-lg bg-green-600/20 text-green-500 border border-green-600/40 hover:bg-green-600/30">
                      Unban
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Profile Modal */}
      {profileModalOpen && userProfile && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setProfileModalOpen(false)}>
          <div className="bg-[#111317] border border-white/10 rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-brand-text">User Profile</h3>
              <button onClick={() => setProfileModalOpen(false)} className="text-brand-muted hover:text-brand-text text-xl">&times;</button>
            </div>

            <div className="space-y-5">
              <div>
                <div className="text-brand-text font-semibold text-xl">{userProfile.name}</div>
                <div className="text-brand-muted">{userProfile.email}</div>
                {userProfile.business_name && <div className="text-sm text-brand-muted mt-1">Business: {userProfile.business_name}</div>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-xs text-brand-muted">Role</div><div className="text-brand-text font-medium">{userProfile.role}</div></div>
                <div><div className="text-xs text-brand-muted">Location</div><div className="text-brand-text">{[userProfile.city, userProfile.region, userProfile.country].filter(Boolean).join(', ') || 'N/A'}</div></div>
                <div><div className="text-xs text-brand-muted">Joined</div><div className="text-brand-text">{new Date(userProfile.created_at).toLocaleDateString()}</div></div>
                <div>
                  <div className="text-xs text-brand-muted">Status</div>
                  <div className="text-brand-text">
                    {userProfile.banned ? <span className="text-red-500">Banned</span>
                      : userProfile.deactivated ? <span className="text-orange-400">Deactivated</span>
                      : <span className="text-green-500">Active</span>}
                  </div>
                </div>
              </div>

              {userProfile.banned && (
                <div className="p-3 rounded-lg bg-red-600/10 border border-red-600/40">
                  <div className="text-red-500 font-semibold text-sm">Ban Details</div>
                  <div className="text-sm text-brand-muted mt-1">Reason: {userProfile.ban_reason}</div>
                  {userProfile.banned_at && <div className="text-xs text-brand-muted mt-1">Banned on: {new Date(userProfile.banned_at).toLocaleString()}</div>}
                </div>
              )}

              {userProfile.role === 'SALESPERSON' && userProfile.stats && (
                <div>
                  <div className="text-sm font-semibold mb-2">Reputation</div>
                  <div className="grid grid-cols-3 gap-4 p-3 bg-brand-bg rounded-lg">
                    <div className="text-center">
                      <div className="text-brand-gold text-lg font-bold">{(userProfile.stats.average_rating || 0).toFixed(1)}</div>
                      <div className="text-xs text-brand-muted">Rating</div>
                    </div>
                    <div className="text-center">
                      <div className="text-brand-text text-lg font-bold">{userProfile.stats.total_reviews || 0}</div>
                      <div className="text-xs text-brand-muted">Reviews</div>
                    </div>
                    <div className="text-center">
                      <div className="text-brand-text text-lg font-bold">{userProfile.requests_count || 0}</div>
                      <div className="text-xs text-brand-muted">Requests</div>
                    </div>
                  </div>
                </div>
              )}

              {userProfile.role === 'BUSINESS' && (
                <div>
                  <div className="text-sm font-semibold mb-2">Activity</div>
                  <div className="grid grid-cols-2 gap-4 p-3 bg-brand-bg rounded-lg">
                    <div className="text-center">
                      <div className="text-brand-text text-lg font-bold">{userProfile.posts_count || 0}</div>
                      <div className="text-xs text-brand-muted">Posts Created</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
                {/* Promote / Demote */}
                {userProfile.role !== 'ADMIN' && (
                  <button onClick={() => handlePromote(userProfile)} disabled={actionLoading} data-testid="admin-user-promote"
                    className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-purple-600/20 text-purple-400 border border-purple-600/40 hover:bg-purple-600/30 disabled:opacity-50">
                    <Crown className="w-3.5 h-3.5" /> Promote to Admin
                  </button>
                )}
                {userProfile.role === 'ADMIN' && userProfile.id !== currentAdmin?.id && (
                  <button onClick={() => handleDemote(userProfile)} disabled={actionLoading} data-testid="admin-user-demote"
                    className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-orange-600/20 text-orange-400 border border-orange-600/40 hover:bg-orange-600/30 disabled:opacity-50">
                    <ArrowDownCircle className="w-3.5 h-3.5" /> Demote Admin
                  </button>
                )}
                {/* Deactivate / Reactivate */}
                {!userProfile.deactivated && userProfile.id !== currentAdmin?.id && (
                  <button onClick={() => handleDeactivate(userProfile)} disabled={actionLoading} data-testid="admin-user-deactivate"
                    className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-orange-600/20 text-orange-400 border border-orange-600/40 hover:bg-orange-600/30 disabled:opacity-50">
                    <UserX className="w-3.5 h-3.5" /> Deactivate
                  </button>
                )}
                {userProfile.deactivated && (
                  <button onClick={() => handleReactivate(userProfile)} disabled={actionLoading} data-testid="admin-user-reactivate"
                    className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-green-600/20 text-green-400 border border-green-600/40 hover:bg-green-600/30 disabled:opacity-50">
                    <UserCheck className="w-3.5 h-3.5" /> Reactivate
                  </button>
                )}
                {/* Ban / Unban */}
                {!userProfile.banned && userProfile.role !== 'ADMIN' && userProfile.id !== currentAdmin?.id && (
                  <button onClick={() => { setSelectedUser(userProfile); setBanModalOpen(true); }} disabled={actionLoading} data-testid="admin-profile-ban"
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-600/20 text-red-500 border border-red-600/40 hover:bg-red-600/30 disabled:opacity-50">
                    Ban User
                  </button>
                )}
                {userProfile.banned && (
                  <button onClick={() => handleUnban(userProfile)} disabled={actionLoading} data-testid="admin-profile-unban"
                    className="px-3 py-1.5 text-sm rounded-lg bg-green-600/20 text-green-500 border border-green-600/40 hover:bg-green-600/30 disabled:opacity-50">
                    Unban User
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ban Modal */}
      {banModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setBanModalOpen(false)}>
          <div className="bg-[#111317] border border-white/10 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Ban User</h3>
            <p className="text-sm text-brand-muted mb-4">Ban {selectedUser?.name}? They will be unable to access the platform.</p>
            <textarea placeholder="Reason for ban (min 10 characters)..." value={banReason} onChange={(e) => setBanReason(e.target.value)} rows={4}
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text resize-none" data-testid="admin-ban-reason" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setBanModalOpen(false)} disabled={actionLoading} className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-brand-text hover:bg-white/5">Cancel</button>
              <button onClick={handleBan} disabled={actionLoading || banReason.length < 10} data-testid="admin-ban-confirm"
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                {actionLoading ? 'Banning...' : 'Ban User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {messageModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setMessageModalOpen(false)}>
          <div className="bg-[#111317] border border-white/10 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Message {selectedUser?.name}</h3>
            <div className="space-y-3">
              <input type="text" placeholder="Subject" value={messageSubject} onChange={(e) => setMessageSubject(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text" data-testid="admin-message-subject" />
              <textarea placeholder="Message" value={messageBody} onChange={(e) => setMessageBody(e.target.value)} rows={5}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text resize-none" data-testid="admin-message-body" />
              <div className="flex gap-2">
                <button onClick={() => setMessageModalOpen(false)} disabled={actionLoading} className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-brand-text hover:bg-white/5">Cancel</button>
                <button onClick={handleSendMessage} disabled={actionLoading} data-testid="admin-message-send"
                  className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">{actionLoading ? 'Sending...' : 'Send'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast Modal */}
      {broadcastModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setBroadcastModalOpen(false)}>
          <div className="bg-[#111317] border border-white/10 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Send Broadcast</h3>
            <div className="space-y-3">
              <select value={broadcastRole} onChange={(e) => setBroadcastRole(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text" data-testid="admin-broadcast-role">
                <option value="ALL">All Users</option>
                <option value="BUSINESS">Businesses Only</option>
                <option value="SALESPERSON">Salespeople Only</option>
              </select>
              <input type="text" placeholder="Subject" value={messageSubject} onChange={(e) => setMessageSubject(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text" data-testid="admin-broadcast-subject" />
              <textarea placeholder="Message" value={messageBody} onChange={(e) => setMessageBody(e.target.value)} rows={5}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text resize-none" data-testid="admin-broadcast-message" />
              <div className="flex gap-2">
                <button onClick={() => setBroadcastModalOpen(false)} disabled={actionLoading} className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-brand-text hover:bg-white/5">Cancel</button>
                <button onClick={handleBroadcast} disabled={actionLoading} data-testid="admin-broadcast-send"
                  className="flex-1 px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700">{actionLoading ? 'Sending...' : 'Send Broadcast'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= BRANDING TAB =============
const BrandingTab = () => {
  const { refreshBrand } = useBrand();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [formData, setFormData] = useState({ logoUrl: '', logoKey: '', primaryHex: '#D4AF37' });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getBrand();
      setSettings(data);
      setFormData({ logoUrl: data.logoUrl || '', logoKey: data.logoKey || '', primaryHex: data.primaryHex || '#D4AF37' });
    } catch (error) { toast.error('Failed to load brand settings'); }
    finally { setLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { toast.error('Please upload a PNG, JPEG, or WebP image'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be less than 2MB'); return; }
    try {
      setSaving(true);
      const { uploadUrl, key, publicUrl } = await adminApi.presignBrandLogo(file.type);
      // LOCAL mode uses multipart POST; S3 mode uses PUT
      if (uploadUrl.includes('/upload-local')) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('key', key);
        const token = localStorage.getItem('token');
        const uploadRes = await fetch(`${(process.env.REACT_APP_BACKEND_URL || '')}${uploadUrl}`, {
          method: 'POST', body: fd,
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!uploadRes.ok) throw new Error('Upload failed');
        const data = await uploadRes.json();
        setFormData(prev => ({ ...prev, logoUrl: data.publicUrl || publicUrl, logoKey: key }));
      } else {
        const uploadRes = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        if (!uploadRes.ok) throw new Error('Upload failed');
        setFormData(prev => ({ ...prev, logoUrl: publicUrl, logoKey: key }));
      }
      toast.success('Logo uploaded');
    } catch (error) { toast.error('Failed to upload logo'); console.error(error); }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (formData.primaryHex && !/^#[0-9A-Fa-f]{6}$/.test(formData.primaryHex)) { toast.error('Invalid hex color'); return; }
    try {
      setSaving(true);
      await adminApi.saveBrand(formData);
      toast.success('Brand settings saved');
      refreshBrand();
      await loadSettings();
    } catch (error) { toast.error('Failed to save brand settings'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    try {
      setResetting(true);
      await adminApi.resetBrand();
      toast.success('Brand reset to defaults');
      refreshBrand();
      await loadSettings();
    } catch (error) { toast.error('Failed to reset brand'); }
    finally { setResetting(false); }
  };

  if (loading) return <div className="text-brand-muted text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Logo */}
      <div className="rounded-xl border border-white/10 bg-[#111317] p-6">
        <h2 className="text-lg font-semibold mb-4">Logo</h2>
        {formData.logoUrl && (
          <div className="mb-4">
            <label className="block text-sm text-brand-muted mb-2">Current Logo</label>
            <img src={formData.logoUrl} alt="Logo preview" className="h-16 object-contain" data-testid="admin-brand-logo" />
          </div>
        )}
        <div>
          <label className="block text-sm text-brand-muted mb-2">Upload New Logo</label>
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} disabled={saving}
            className="block w-full text-sm text-brand-text file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-brand-gold file:text-black hover:file:bg-brand-gold/90 disabled:opacity-50" />
          <p className="text-xs text-brand-muted mt-1">PNG, JPEG, or WebP. Max 2MB.</p>
        </div>
      </div>

      {/* Primary Color */}
      <div className="rounded-xl border border-white/10 bg-[#111317] p-6">
        <h2 className="text-lg font-semibold mb-4">Primary Color</h2>
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm text-brand-muted mb-2">Hex Code</label>
            <input type="text" value={formData.primaryHex} onChange={(e) => setFormData(prev => ({ ...prev, primaryHex: e.target.value }))} placeholder="#D4AF37"
              className="w-32 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text" data-testid="admin-brand-color" />
          </div>
          <div>
            <label className="block text-sm text-brand-muted mb-2">Preview</label>
            <div className="w-16 h-10 rounded-lg border border-white/10" style={{ backgroundColor: formData.primaryHex }} />
          </div>
          <div>
            <label className="block text-sm text-brand-muted mb-2">Picker</label>
            <input type="color" value={formData.primaryHex} onChange={(e) => setFormData(prev => ({ ...prev, primaryHex: e.target.value }))}
              className="w-10 h-10 rounded border-0 cursor-pointer" />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button onClick={handleSave} disabled={saving || resetting} data-testid="admin-brand-save"
          className="px-4 py-2 rounded-xl bg-brand-gold text-black hover:bg-brand-gold/90 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={handleReset} disabled={saving || resetting} data-testid="admin-brand-reset"
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-red-600/40 text-red-500 hover:bg-red-600/10 disabled:opacity-50">
          <RotateCcw className="w-4 h-4" />
          {resetting ? 'Resetting...' : 'Reset to Default'}
        </button>
      </div>
    </div>
  );
};

// ============= MODERATION TAB =============
const ModerationTab = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('OPEN');
  const [modStats, setModStats] = useState(null);

  useEffect(() => { loadFlags(); loadModStats(); }, [filter]);

  const loadFlags = async () => {
    try { setLoading(true); const data = await adminApi.getFlags(filter); setFlags(data); }
    catch (error) { toast.error('Failed to load flags'); }
    finally { setLoading(false); }
  };

  const loadModStats = async () => {
    try { const data = await adminApi.getModerationStats(); setModStats(data); }
    catch (error) { /* silently fail */ }
  };

  const handleResolve = async (reviewId, action) => {
    try {
      await adminApi.resolveFlag(reviewId, action);
      toast.success(`Review ${action.toLowerCase()}ed`);
      loadFlags(); loadModStats();
    } catch (error) { toast.error('Failed to resolve flag'); }
  };

  return (
    <div className="space-y-6">
      {/* Moderation Stats */}
      {modStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-mod-stats">
          <StatCard title="Open Flags" value={modStats.flags.open} variant="warning" testid="admin-mod-flags-open" />
          <StatCard title="Resolved" value={modStats.flags.resolved} testid="admin-mod-flags-resolved" />
          <StatCard title="Banned Users" value={modStats.banned_users} variant="danger" testid="admin-mod-banned" />
          <StatCard title="Deactivated" value={modStats.deactivated_users} testid="admin-mod-deactivated" />
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <label className="text-sm text-brand-muted">Flag Status:</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-mod-filter"
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text">
          <option value="OPEN">Open</option>
          <option value="RESOLVED">Resolved</option>
          <option value="DISMISSED">Dismissed</option>
        </select>
      </div>

      {/* Flags List */}
      {loading ? (
        <div className="text-brand-muted text-center py-12">Loading flags...</div>
      ) : flags.length === 0 ? (
        <div className="text-center py-12 text-brand-muted">No {filter.toLowerCase()} flags found</div>
      ) : (
        <div className="space-y-4">
          {flags.map((flag) => (
            <div key={flag.id} data-testid="admin-flag-row" className="rounded-xl border border-white/10 bg-[#111317] p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-brand-gold font-semibold">{'*'.repeat(flag.review_stars)}</span>
                    <span className="text-xs px-2 py-1 rounded bg-zinc-800 text-brand-text">by {flag.reviewer_name}</span>
                  </div>
                  {flag.review_comment && <p className="text-sm text-brand-text mb-3">{flag.review_comment}</p>}
                  <div className="text-xs text-brand-muted space-y-0.5">
                    <div>Reported by: {flag.reporter_name}</div>
                    <div>Reason: {flag.reason}</div>
                    <div>{new Date(flag.created_at).toLocaleString()}</div>
                  </div>
                </div>
                {flag.status === 'OPEN' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => handleResolve(flag.review_id, 'MASK')} data-testid="admin-flag-action-mask"
                      className="px-3 py-1 text-xs rounded-lg bg-yellow-600/20 text-yellow-500 border border-yellow-600/40 hover:bg-yellow-600/30">Mask</button>
                    <button onClick={() => handleResolve(flag.review_id, 'REMOVE')} data-testid="admin-flag-action-remove"
                      className="px-3 py-1 text-xs rounded-lg bg-red-600/20 text-red-500 border border-red-600/40 hover:bg-red-600/30">Remove</button>
                    <button onClick={() => handleResolve(flag.review_id, 'DISMISS')} data-testid="admin-flag-action-dismiss"
                      className="px-3 py-1 text-xs rounded-lg bg-zinc-800 text-brand-text border border-zinc-700 hover:bg-zinc-700">Dismiss</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============= ACTIVITY TAB =============
const ActivityTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try { setLoading(true); const data = await adminApi.getAdminStats(); setStats(data); }
    catch (error) { toast.error('Failed to load stats'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-brand-muted text-center py-12">Loading stats...</div>;
  if (!stats) return <div className="text-brand-muted text-center py-12">Failed to load stats</div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Users</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-stat-users">
          <StatCard title="Total Users" value={stats.users.total} testid="admin-stat-users-total" />
          <StatCard title="Businesses" value={stats.users.business} testid="admin-stat-users-business" />
          <StatCard title="Salespeople" value={stats.users.sales} testid="admin-stat-users-sales" />
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-4">Posts</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-stat-posts">
          <StatCard title="Total Posts" value={stats.posts.total} testid="admin-stat-posts-total" />
          <StatCard title="Open Posts" value={stats.posts.open} testid="admin-stat-posts-open" />
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-4">Sales Requests</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-stat-requests">
          <StatCard title="Requested" value={stats.requests.requested} testid="admin-stat-requests-requested" />
          <StatCard title="Won" value={stats.requests.won} subtitle="Closed as WON" testid="admin-stat-requests-won" />
          <StatCard title="Lost" value={stats.requests.lost} subtitle="Closed as LOST" testid="admin-stat-requests-lost" />
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-4">Reviews</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-stat-reviews">
          <StatCard title="Total Reviews" value={stats.reviews.count} testid="admin-stat-reviews-count" />
          <StatCard title="Average Rating" value={stats.reviews.avg.toFixed(1)} subtitle="Out of 5.0" testid="admin-stat-reviews-avg" />
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-4">Activity</h3>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <StatCard title="Active Users (Last 7 Days)" value={stats.active7d} testid="admin-stat-active7d" />
        </div>
      </div>
    </div>
  );
};

// ============= AUDIT LOG TAB =============
const AuditTab = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => { loadLogs(); }, [actionFilter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getAuditLog(50, 0, actionFilter);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  };

  const actionColors = {
    BAN_USER: 'text-red-500',
    UNBAN_USER: 'text-green-500',
    PROMOTE_ADMIN: 'text-purple-400',
    DEMOTE_ADMIN: 'text-orange-400',
    DEACTIVATE_USER: 'text-orange-400',
    REACTIVATE_USER: 'text-green-400',
    BRAND_UPDATE: 'text-blue-400',
    BRAND_RESET: 'text-blue-400',
    REVIEW_MASK: 'text-yellow-500',
    REVIEW_REMOVE: 'text-red-400',
    REVIEW_DISMISS: 'text-zinc-400',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Audit Trail ({total} entries)</h3>
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} data-testid="admin-audit-filter"
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text text-sm">
          <option value="">All Actions</option>
          <option value="BAN_USER">Ban</option>
          <option value="UNBAN_USER">Unban</option>
          <option value="PROMOTE_ADMIN">Promote</option>
          <option value="DEMOTE_ADMIN">Demote</option>
          <option value="DEACTIVATE_USER">Deactivate</option>
          <option value="REACTIVATE_USER">Reactivate</option>
          <option value="BRAND_UPDATE">Brand Update</option>
          <option value="BRAND_RESET">Brand Reset</option>
          <option value="REVIEW_MASK">Review Mask</option>
          <option value="REVIEW_REMOVE">Review Remove</option>
          <option value="REVIEW_DISMISS">Review Dismiss</option>
        </select>
      </div>

      {loading ? (
        <div className="text-brand-muted text-center py-12">Loading audit log...</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-brand-muted">No audit entries found</div>
      ) : (
        <div className="space-y-2">
          {logs.map((log, idx) => (
            <div key={idx} data-testid="admin-audit-row" className="rounded-lg border border-white/10 bg-[#111317] p-4 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-semibold ${actionColors[log.action] || 'text-brand-text'}`}>
                    {log.action?.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-brand-muted">by {log.actor_name || 'System'}</span>
                </div>
                <div className="text-sm text-brand-muted mt-1">
                  Target: {log.target_name || log.target_id} &mdash; {log.details}
                </div>
              </div>
              <div className="text-xs text-brand-muted whitespace-nowrap">
                {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============= SHARED: STAT CARD =============
const StatCard = ({ title, value, subtitle, testid, variant }) => {
  const borderColor = variant === 'warning' ? 'border-yellow-600/30' : variant === 'danger' ? 'border-red-600/30' : 'border-white/10';
  return (
    <div className={`rounded-xl border ${borderColor} bg-[#111317] p-6`} data-testid={testid}>
      <div className="text-sm text-brand-muted mb-2">{title}</div>
      <div className="text-3xl font-bold text-brand-text mb-1">{value}</div>
      {subtitle && <div className="text-xs text-brand-muted">{subtitle}</div>}
    </div>
  );
};

export default AdminConsole;
